import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  ImageList,
  ImageListItem,
  ImageListItemBar,
} from '@mui/material';
import { AutoAwesome, Image as ImageIcon, Search } from '@mui/icons-material';
import { ImageCandidate } from '../../types/api';

interface ImageSearchPanelProps {
  topic: string;
  postContent: string;
  defaultQuery?: string;
  orientation: 'landscape' | 'portrait' | 'squarish';
  onOrientationChange: (value: 'landscape' | 'portrait' | 'squarish') => void;
  imageQuery: string;
  onImageQueryChange: (value: string) => void;
  suggestions: string[];
  candidates: ImageCandidate[];
  selectedPreviewUrl?: string;
  loadingSuggestions: boolean;
  loadingSearch: boolean;
  selectingImage: boolean;
  onSuggestTitles: () => void;
  onSearchImages: () => void;
  onSelectSuggestion: (query: string) => void;
  onSelectImage: (candidate: ImageCandidate) => void;
}

export const ImageSearchPanel: React.FC<ImageSearchPanelProps> = ({
  topic,
  postContent,
  defaultQuery,
  orientation,
  onOrientationChange,
  imageQuery,
  onImageQueryChange,
  suggestions,
  candidates,
  selectedPreviewUrl,
  loadingSuggestions,
  loadingSearch,
  selectingImage,
  onSuggestTitles,
  onSearchImages,
  onSelectSuggestion,
  onSelectImage,
}) => {
  const canSuggest = Boolean(topic.trim() && postContent.trim());

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Image Search
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Use short visual keywords (not full post titles). Suggest titles from your post, edit them, then search and pick an image.
      </Typography>

      {defaultQuery && (defaultQuery.length > 40 || defaultQuery.split(/\s+/).length > 5) && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Auto-generated terms may be too long for image search. Use <strong>Suggest titles</strong> for short visual keywords.
        </Alert>
      )}

      <TextField
        fullWidth
        label="Image search title"
        value={imageQuery}
        onChange={(e) => onImageQueryChange(e.target.value)}
        placeholder="e.g. digital marketing team, office collaboration"
        sx={{ mb: 2 }}
        helperText="2–4 words work best on Unsplash"
      />

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Orientation</InputLabel>
        <Select
          value={orientation}
          label="Orientation"
          onChange={(e) => onOrientationChange(e.target.value as 'landscape' | 'portrait' | 'squarish')}
        >
          <MenuItem value="landscape">Landscape</MenuItem>
          <MenuItem value="portrait">Portrait</MenuItem>
          <MenuItem value="squarish">Square</MenuItem>
        </Select>
      </FormControl>

      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={loadingSuggestions ? <CircularProgress size={16} /> : <AutoAwesome />}
          onClick={onSuggestTitles}
          disabled={!canSuggest || loadingSuggestions}
          fullWidth
        >
          Suggest titles
        </Button>
        <Button
          variant="contained"
          startIcon={loadingSearch ? <CircularProgress size={16} /> : <Search />}
          onClick={onSearchImages}
          disabled={!imageQuery.trim() || loadingSearch}
          fullWidth
        >
          Search images
        </Button>
      </Box>

      {suggestions.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Suggested search titles
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {suggestions.map((suggestion) => (
              <Chip
                key={suggestion}
                label={suggestion}
                clickable
                color={imageQuery === suggestion ? 'primary' : 'default'}
                onClick={() => onSelectSuggestion(suggestion)}
                icon={<ImageIcon />}
              />
            ))}
          </Box>
        </Box>
      )}

      {candidates.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Pick an image ({candidates.length} results)
          </Typography>
          <ImageList cols={2} gap={8} sx={{ maxHeight: 360, overflow: 'auto' }}>
            {candidates.map((candidate) => (
              <ImageListItem
                key={candidate.id}
                sx={{
                  cursor: selectingImage ? 'wait' : 'pointer',
                  outline: selectedPreviewUrl === candidate.preview_url ? '3px solid' : 'none',
                  outlineColor: 'primary.main',
                  borderRadius: 1,
                }}
                onClick={() => !selectingImage && onSelectImage(candidate)}
              >
                <img
                  src={candidate.preview_url}
                  alt={candidate.query}
                  loading="lazy"
                  style={{ height: 140, objectFit: 'cover', width: '100%' }}
                />
                <ImageListItemBar title={candidate.query} subtitle={candidate.credit} />
              </ImageListItem>
            ))}
          </ImageList>
        </Box>
      )}

      {loadingSearch && candidates.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={28} />
        </Box>
      )}
    </Box>
  );
};

export default ImageSearchPanel;
